import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import nopreview from "../../assets/images/others/no-preview.png";
import { useGetEcgDetails } from "../../api/EcgReports";
import { BlankPreview } from "../../components/BlankPreview";
import { ReportPreview } from "../../components/ReportPreview";
import Skeleton from "@mui/material/Skeleton";
import {
  ANALYTICS_EVENTS,
  FULL_REPORT_SOURCE,
  logAnalyticsEvent,
} from "../../analytics";
import {
  Box,
  Breadcrumbs,
  Typography,
  Link as MuiLink,
} from "@mui/material";

function ECGFullReports() {
  const [previewReport, setPreviewReport] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { EcgReport, isLoading } = useGetEcgDetails({}, id);

  // Keyed on the report id alone: a re-render, or the preview toggle below,
  // must not count as another view of the same report.
  useEffect(() => {
    if (!id) return;
    logAnalyticsEvent(ANALYTICS_EVENTS.FULL_REPORT_VIEWED, {
      report_id: id,
      source: location.state?.analyticsSource || FULL_REPORT_SOURCE.DIRECT,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const getRequestStatusStyles = () => {
    const status = EcgReport?.interpretation_request_status;

    if (status === "requested" || status === "in progress") {
      return {
        label: "Awaiting",
        bg: "#FFF3E0",
        color: "#F48C06",
        border: "1px solid #DC760761",
      };
    }

    if (status === "completed") {
      return {
        label: "Interpreted",
        bg: "#E4F6E9",
        color: "#2E7D32",
        border: "1px solid #3485301F",
      };
    }

    return {
      label: "Not Interpreted",
      bg: "#FFE4E4",
      color: "#D32F2F",
      border: "1px solid #0000000A",
    };
  };

  const {
    label: reqLabel,
    bg: reqBg,
    color: reqColor,
    border: reqBorder,
  } = getRequestStatusStyles();

  return (
    <>
      <Helmet>
        <title>Spandan - ECG Reports</title>
      </Helmet>

      <div className="main-wrappers">
        <div className="page-wrapper" style={{ fontFamily: "Figtree" }}>
          {isLoading ? (
            <div className="p-3">
              <div className="row">
                {[1, 2, 3, 4, 5, 6, 7].map((_, idx) => (
                  <div key={idx} className=" mb-4">
                    <Skeleton variant="rectangular" animation="wave" height={100} width="100%" style={{
                      borderRadius: "10px",
                      boxShadow: "var(--app-shadow)",
                    }}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="content">
              <br />
              <div className="d-flex justify-content-between align-items-center">
                <Box>
                  {/* ===== BREADCRUMB ===== */}
                  <Breadcrumbs
                    sx={{
                      mb: 2,
                      mt: -2,
                      "& .MuiBreadcrumbs-li": {
                        display: "flex",
                        alignItems: "center",
                      },
                    }}
                  >
                    <MuiLink
                      component="button"
                      underline="hover"
                      onClick={() => navigate("/ecg-reports")}
                      sx={{
                        cursor: "pointer",
                        fontFamily: "Figtree",
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "20px",
                        letterSpacing: "-0.02em",
                        color: "var(--app-text)",
                      }}
                    >
                      ECG Reports
                    </MuiLink>

                    <Typography
                      component="span"
                      sx={{
                        fontFamily: "Figtree",
                        fontWeight: 500,
                        fontSize: "14px",
                        lineHeight: "20px",
                        letterSpacing: "-0.02em",
                        color: "var(--app-text)",
                      }}
                    >
                      {EcgReport?.collection_id || "—"}
                    </Typography>
                  </Breadcrumbs>

                  {/* ===== HEADER ROW (MUI GRID STYLE) ===== */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mt: 1,
                      gap: 2,
                      mb: -2,
                    }}
                  >
                    {/* LEFT : Back + Title */}
                    <Box
                      onClick={() => navigate(-1)}
                      sx={{
                        position: "relative",
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: "pointer",

                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: "-10px",
                          bottom: "-10px",
                          left: "-12px",
                          right: "-12px",
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "20px",
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          color: "var(--app-text)",
                        }}
                      >
                        <i className="fa fa-angle-left" />#
                        {EcgReport?.collection_id} |{" "}
                        {EcgReport?.user_data?.first_name +
                          " " +
                          EcgReport?.user_data?.last_name}
                      </Typography>
                    </Box>

                    {/* CENTER : STATUS */}
                    <Box>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.4,
                          borderRadius: 1,
                          bgcolor: reqBg,
                          color: reqColor,
                          border: reqBorder,
                          fontSize: 12,
                          fontWeight: 600,
                          fontFamily: "Figtree",
                        }}
                      >
                        {reqLabel}
                      </Box>
                    </Box>

                    {/* RIGHT : CRITICAL ECG BADGE (MUI CHIP) */}
                    {/* <Chip
      label="Critical ECG"
      sx={{
        backgroundColor: "#FDECEC",
        color: "#D32F2F",
        fontWeight: 600,
        borderRadius: "6px",
        height: "28px",
      }}
    /> */}
                  </Box>
                </Box>

                <div>
                  {EcgReport?.conclusions?.ecg_type && (
                    <strong
                      className={
                        EcgReport?.conclusions.ecg_type === "Normal ECG"
                          ? "gg-success"
                          : "gg-danger"
                      }
                      style={{ fontSize: "18px" }}
                    >
                      {EcgReport?.conclusions.ecg_type}
                    </strong>
                  )}
                </div>
              </div>

              <div className="row">
                <div className="col-md-12">
                  {previewReport ? (
                    <ReportPreview type="full" Link={Link} data={EcgReport} />
                  ) : (
                    <BlankPreview
                      icon={nopreview}
                      title="No Preview Available"
                      subtitle="Select a report to preview"
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default ECGFullReports;
